import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, CheckSquare, MessageSquare, Clock, FolderKanban, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { useAuth } from '@/context/AuthContext.js';

export function OnboardingChecklist() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isDismissed, setIsDismissed] = useState(true);
  const [completedSteps, setCompletedSteps] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!user) return;
    try {
      const dismissed = localStorage.getItem(`tsos_onboarding_dismissed_${user.id}`);
      setIsDismissed(!!dismissed);

      const savedSteps = localStorage.getItem(`tsos_onboarding_steps_${user.id}`);
      if (savedSteps) {
        setCompletedSteps(JSON.parse(savedSteps));
      }
    } catch {}
  }, [user]);

  if (isDismissed || !user) return null;

  const toggleStep = (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = { ...completedSteps, [key]: !completedSteps[key] };
    setCompletedSteps(updated);
    try {
      localStorage.setItem(`tsos_onboarding_steps_${user.id}`, JSON.stringify(updated));
    } catch {}
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    try {
      localStorage.setItem(`tsos_onboarding_dismissed_${user.id}`, 'true');
    } catch {}
  };

  const steps = [
    {
      key: 'tasks',
      title: 'Review your assigned tasks',
      desc: 'See deliverables assigned to you and update their progress',
      path: '/my-tasks',
      icon: CheckSquare,
    },
    {
      key: 'standup',
      title: 'Submit your daily standup',
      desc: 'Keep the team updated on your blockers and achievements',
      path: '/standups',
      icon: MessageSquare,
    },
    {
      key: 'time',
      title: 'Track work time with the live timer',
      desc: 'Start and stop timer sessions on your active tasks',
      path: '/time',
      icon: Clock,
    },
    {
      key: 'projects',
      title: 'Explore agency projects',
      desc: 'Check scopes, deadlines, and milestone progress',
      path: '/projects',
      icon: FolderKanban,
    },
  ];

  const total = steps.length;
  const completedCount = steps.filter((s) => completedSteps[s.key]).length;
  const progressPercent = Math.round((completedCount / total) * 100);

  return (
    <div className="bg-brand-surface dark:bg-[#12211F] rounded-xl border border-brand-border p-4 sm:p-5 shadow-sm space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-2.5">
          <div className="p-2 rounded-lg bg-brand-tint text-brand">
            <Sparkles className="h-5 w-5 text-brand" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-brand-ink">
              Welcome to Project OS, {user.name}
            </h3>
            <p className="text-xs text-brand-muted">
              Quick start checklist to get up and running with agency workflows
            </p>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-brand-muted hover:text-brand-ink p-1.5 rounded-lg min-h-[36px] min-w-[36px] flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800"
          title="Dismiss walkthrough"
          aria-label="Dismiss walkthrough"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs">
          <span className="text-brand-muted font-medium">Onboarding Progress</span>
          <span className="font-mono font-bold text-brand">{completedCount} of {total} completed ({progressPercent}%)</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
          <div
            className="h-full bg-brand rounded-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Steps List */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
        {steps.map((s) => {
          const isDone = Boolean(completedSteps[s.key]);
          const Icon = s.icon;
          return (
            <div
              key={s.key}
              onClick={() => navigate(s.path)}
              className={`p-3 rounded-xl border transition-all cursor-pointer flex items-start space-x-3 text-left ${
                isDone
                  ? 'bg-slate-50/60 dark:bg-slate-800/20 border-slate-200 dark:border-slate-800 opacity-80'
                  : 'bg-white dark:bg-[#152724] border-brand-border hover:border-brand shadow-none hover:shadow-sm'
              }`}
            >
              <button
                type="button"
                onClick={(e) => toggleStep(s.key, e)}
                className="mt-0.5 text-brand hover:scale-110 transition-transform min-h-[28px] min-w-[28px] flex items-center justify-center"
                aria-label={isDone ? 'Mark as incomplete' : 'Mark as complete'}
              >
                {isDone ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 fill-emerald-100" />
                ) : (
                  <Circle className="h-5 w-5 text-brand-muted" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-1.5">
                  <Icon className="h-3.5 w-3.5 text-brand-muted" />
                  <span className={`text-xs font-bold truncate ${isDone ? 'line-through text-brand-muted' : 'text-brand-ink'}`}>
                    {s.title}
                  </span>
                </div>
                <p className="text-[11px] text-brand-muted mt-0.5 leading-snug line-clamp-1">
                  {s.desc}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-1 text-[11px] text-brand-muted">
        <span>Click any step to jump straight to the module</span>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleDismiss}
          className="h-7 text-xs text-brand-muted hover:text-brand-ink px-2"
        >
          Don't show again
        </Button>
      </div>
    </div>
  );
}

export default OnboardingChecklist;
